import { useContext, useEffect, useMemo, useState } from "react";

import {
  EdaMetaDataContext,
  IFilterStatsTaskData,
  ViewFiltersRequest,
  useGetFilterStatsMutation,
  useGetRequestStatusQuery,
} from "@/features/data-transformation";
import { POLLING_STATUS } from "@/utils/enums";

const POLLING_INTERVAL = 500;

interface UsePaginatedFilterStatsProps {
  column: string | null;
  isOpen: boolean;
  filters?: ViewFiltersRequest[];
  transformationStepId?: string;
}

interface PaginationState {
  hasMore: boolean;
  isLoading: boolean;
  isPaginating: boolean; 
  search: string;
  lastCount: number;
  lastValue: string;
  lastHash: string;
}

const initialPaginationState: PaginationState = {
  hasMore: false,
  isLoading: false,
  isPaginating: false,
  search: "",
  lastCount: -1,
  lastValue: "",
  lastHash: "",
};

export const usePaginatedFilterStats = ({
  column,
  isOpen,
  filters = [],
  transformationStepId,
}: UsePaginatedFilterStatsProps) => {
  const metaData = useContext(EdaMetaDataContext);
  const [filterStatsData, setFilterStatsData] =
    useState<IFilterStatsTaskData | null>(null);
  const [filterItems, setFilterItems] = useState<string[] | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [paginationState, setPaginationState] = useState<PaginationState>(
    initialPaginationState
  );

  const [getStats, { isLoading: isFetchingStats }] =
    useGetFilterStatsMutation();

  const { data: runStatusData, isFetching: isPolling } = useGetRequestStatusQuery(
    {
      analysisId: metaData?.analysisId ?? "",
      edaId: metaData?.edaId ?? "",
      requestId: requestId!,
    },
    {
      pollingInterval: POLLING_INTERVAL,
      skip: requestId == null || !metaData?.analysisId || !metaData?.edaId,
    }
  );

  // Handle search value change - reset paginated data and fetch first page
  const handleSearchChange = (search: string) => {
    const trimmedSearch = search.trim();

    // Only proceed if the trimmed search term is different from the current one
    if (trimmedSearch === paginationState.search) {
      return;
    }

    setPaginationState({
      ...initialPaginationState,
      hasMore: false,
      isLoading: true,
      isPaginating: false,
      search: trimmedSearch,
    });

    // Clear existing data for new search
    setFilterStatsData(null);
    setFilterItems(null);

    void fetchFilterStats(trimmedSearch);
  };

  // Fetch next page of data
  const fetchNextPage = () => {
    if (!paginationState.hasMore || paginationState.isLoading || paginationState.isPaginating) return;

    setPaginationState((prev) => ({
      ...prev,
      isPaginating: true,
    }));

    void fetchFilterStats(
      paginationState.search,
      paginationState.lastCount,
      paginationState.lastValue,
      paginationState.lastHash,
    );
  };

  // Reset all data and pagination state
  const resetData = () => {
    setFilterStatsData(null);
    setFilterItems(null);
    setPaginationState(initialPaginationState);
  };

  // Core function to fetch filter stats from API
  const fetchFilterStats = async (
    search: string = "",
    lastCount: number = -1,
    lastValue: string = "",
    lastHash: string = "",
  ) => {
    if (!metaData?.edaId || !metaData?.analysisId || !column) return;

    try {
      // Set initial loading state if it's not a pagination call
      if (lastCount === -1) {
        setPaginationState((prev) => ({
          ...prev,
          isLoading: true,
          isPaginating: false,
        }));
      }

      const res = await getStats({
        edaId: metaData.edaId,
        analysisId: metaData.analysisId,
        column,
        viewFilters: filters,
        transformationStepId,
        search,
        lastCount,
        lastValue,
        lastHash,
      }).unwrap();

      const id = res.response.data?.requestId ?? null;
      setRequestId(id);
      // isLoading/isPaginating will be set to false when polling completes or fails
    } catch (e) {
      console.error("Error fetching filter stats:", e);
      setPaginationState((prev) => ({
        ...prev,
        isLoading: false,
        isPaginating: false,
      }));
    }
  };

  // Initial fetch when component mounts or column changes
  useEffect(() => {
    const hasRequiredData =
      !!metaData?.edaId && !!metaData?.analysisId && !!column;
    const shouldFetchFilterValues = hasRequiredData && isOpen;

    if (shouldFetchFilterValues) {
      resetData();
      void fetchFilterStats(); // This will set isLoading: true internally
    }
  }, [column, metaData?.edaId, metaData?.analysisId, isOpen]);

  // Process data from polling response
  useEffect(() => {
    if (!requestId || !runStatusData?.response?.data) return;

    if (runStatusData.response.data.taskStatus === POLLING_STATUS.FAILED) {
      setRequestId(null); // Stop polling on failure
      setPaginationState((prev) => ({
        ...prev,
        isLoading: false,
        isPaginating: false,
      }));
      return;
    }

    if (runStatusData.response.data.taskStatus === POLLING_STATUS.COMPLETED) {
        setRequestId(null); 
       processPollingResponse(); // Now we know runStatusData corresponds to requestId
    }
  }, [runStatusData]); // Dependency only on runStatusData

  // Process the data returned from polling
  const processPollingResponse = () => {
    const taskData = runStatusData!.response.data!
      .taskData as IFilterStatsTaskData;

    if (!taskData) {
      console.error("Polling completed but taskData is missing.");
      setRequestId(null);
      setPaginationState(prev => ({ ...prev, isLoading: false, isPaginating: false }));
      return;
    }
   

    const { uniqueValues = [], pagination } = taskData;
    const values = uniqueValues.map((item) => item.value);
    // If this is a new search or initial fetch, replace all data
    if (paginationState.lastCount === -1) {
      setFilterStatsData(taskData);
      setFilterItems(values);
    } else {
      setFilterStatsData((prev) => {
        if (!prev) {
            return taskData; // Fallback for safety
        }

        return {
          ...prev,
          uniqueValues: [...prev.uniqueValues, ...taskData.uniqueValues], 
          pagination: pagination, 
        };
      });
      setFilterItems((prev) => {
        if (!prev) {
            return values; 
        }
        const combined = [...prev, ...values];
        return combined; 
      });
    }

    // Update final pagination state using the nextCursor from the API response
    setPaginationState((prev) => {
        return {
            ...prev,
            hasMore: pagination.hasMore,
            isLoading: false,
            isPaginating: false,
            // Use nextCursor from the pagination object for the next request
            lastCount: pagination.nextCursor?.lastCount ?? -1,
            lastValue: pagination.nextCursor?.lastValue ?? "",
            lastHash: pagination.nextCursor?.lastHash ?? "",
        };
    });
  };

  // Calculate overall loading state
  const isLoading =
    paginationState.isLoading || isPolling || isFetchingStats;

  const totalValuesCount = useMemo(() => filterStatsData?.totalValuesCount ?? 0, [filterStatsData?.totalValuesCount]);


  return {
    filterStatsData,
    totalValuesCount,
    filterItems,
    isLoading, // Use this for the overall loading indicator (initial/search)
    isPaginating:
      paginationState.isPaginating || (isPolling && paginationState.lastCount !== -1), // Use this for the "Load More" button spinner
    hasMore: paginationState.hasMore,
    setFilterItems,
    handleSearchChange,
    fetchNextPage,
    resetData,
  };
};
